import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, statusBarClass } from "@/components/app/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Clock, X, Loader2, Camera, AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "assigned", "in_progress", "resolved", "closed", "reopened"];
const CATEGORIES = [
  "Plumbing", "Electrical", "Carpentry", "Civil",
  "Painting", "Swimming Pool", "Water Fountains", "Housekeeping",
  "Front Office", "F&B", "Security", "IT / Systems", "Other",
];
const ROOM_NUMBERS = ["101", "102", "103", "104", "105", "Common Washroom"];

// ISO string → value for <input type="datetime-local"> (local time, no tz).
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function Tickets() {
  const { selectedId, properties } = useProperty();
  const { user, hasPermission } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [zones, setZones] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [assignMode, setAssignMode] = useState("individual"); // individual | department
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", category: "", description: "", priority: "medium", zone_id: "", room_no: "", assigned_to: "", assigned_to_department: "", photo_paths: [] });
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [active, setActive] = useState(null);
  const [resOpen, setResOpen] = useState(false);
  const [resolution, setResolution] = useState({ resolution_notes: "", resolution_photo_paths: [] });
  const [etaFor, setEtaFor] = useState(null);
  const [etaValue, setEtaValue] = useState("");

  const load = useCallback(async () => {
    if (!selectedId) return;
    const [t, s, z, d] = await Promise.all([
      api.get(`/tickets?property_id=${selectedId}`),
      api.get(`/users?property_id=${selectedId}`).catch(() => ({ data: [] })),
      api.get(`/zones?property_id=${selectedId}`).catch(() => ({ data: [] })),
      api.get(`/departments`).catch(() => ({ data: [] })),
    ]);
    setTickets(t.data); setStaff(s.data); setZones(z.data);
    setDepartments((d.data || []).map((x) => (typeof x === "string" ? x : x.name)));
  }, [selectedId]);
  useEffect(() => { load(); }, [load]);

  // When the New Ticket dialog opens, default the property to the currently
  // selected one. When the user picks a different property inside the dialog,
  // refresh the zone + maintenance lists for that property so the dependent
  // dropdowns stay in sync.
  useEffect(() => {
    if (open) setForm((f) => ({ ...f, property_id: f.property_id || selectedId || "" }));
  }, [open, selectedId]);
  useEffect(() => {
    const pid = form.property_id;
    if (!open || !pid || pid === selectedId) return;
    let cancelled = false;
    (async () => {
      try {
        const [s, z] = await Promise.all([
          api.get(`/users?property_id=${pid}`),
          api.get(`/zones?property_id=${pid}`),
        ]);
        if (cancelled) return;
        setStaff(s.data); setZones(z.data);
        // If the previously-picked zone/assignee is no longer valid for the
        // new property, clear them.
        setForm((f) => ({
          ...f,
          zone_id: z.data.find((x) => x.id === f.zone_id) ? f.zone_id : "",
          assigned_to: s.data.find((x) => x.id === f.assigned_to) ? f.assigned_to : "",
        }));
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.property_id, open]);

  const assignableStaff = useMemo(() => staff.filter((s) => s.active !== false && s.role !== "super_admin"), [staff]);
  const ticketDepartments = useMemo(() => (
    Array.from(new Set(tickets.map((t) => t.assigned_to_department).filter(Boolean))).sort()
  ), [tickets]);
  const filtered = useMemo(() => (
    tickets.filter((t) => (
      (filterStatus === "all" || t.status === filterStatus) &&
      (filterDept === "all" || t.assigned_to_department === filterDept)
    ))
  ), [tickets, filterStatus, filterDept]);

  const submit = async () => {
    if (!form.property_id) { toast.error("Pick a property"); return; }
    if (!form.category) { toast.error("Pick a category"); return; }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    try {
      await api.post(`/tickets`, {
        ...form,
        assigned_to: assignMode === "individual" ? (form.assigned_to || null) : null,
        assigned_to_department: assignMode === "department" ? (form.assigned_to_department || null) : null,
        zone_id: form.zone_id || null,
        room_no: form.room_no || null,
      });
      toast.success("Ticket created");
      setOpen(false);
      setForm({ property_id: "", category: "", description: "", priority: "medium", zone_id: "", room_no: "", assigned_to: "", assigned_to_department: "", photo_paths: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const acceptTicket = async (tid) => {
    try {
      const { data } = await api.post(`/tickets/${tid}/accept`);
      toast.success("Ticket accepted — now set your expected closing time");
      await load();
      openEta(data || null, tid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept ticket");
    }
  };

  const openEta = (ticket, tid) => {
    const t = ticket || tickets.find((x) => x.id === tid);
    if (!t) return;
    setEtaFor(t);
    setEtaValue(toLocalInput(t.expected_closing_at));
  };

  const saveEta = async () => {
    if (!etaValue) { toast.error("Pick a date & time"); return; }
    try {
      await api.put(`/tickets/${etaFor.id}/eta`, { expected_closing_at: new Date(etaValue).toISOString() });
      toast.success("Expected closing time saved");
      setEtaFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set closing time");
    }
  };

  const updateStatus = async (tid, status) => {
    await api.put(`/tickets/${tid}`, { status });
    toast.success("Updated");
    load();
  };

  const assign = async (tid, uid) => {
    await api.put(`/tickets/${tid}`, { assigned_to: uid, status: "assigned" });
    toast.success("Assigned");
    load();
  };

  const resolve = async () => {
    if (!active) return;
    await api.put(`/tickets/${active.id}`, {
      status: "resolved",
      resolution_notes: resolution.resolution_notes,
      resolution_photo_paths: resolution.resolution_photo_paths,
    });
    toast.success("Resolved");
    setResOpen(false); setResolution({ resolution_notes: "", resolution_photo_paths: [] });
    load();
  };

  const canCreate = hasPermission("tickets_create");
  const isManager = user?.role === "supervisor" || user?.role === "super_admin" || user?.role === "general_manager" || user?.role === "property_admin";

  if (!selectedId) return <div className="text-zinc-500">Select a property.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="label-overline">Operations</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Tickets</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 hover:bg-zinc-800" data-testid="new-ticket-button"><Plus className="w-4 h-4 mr-2" /> New Ticket</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Ticket</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <div>
                  <Label>Property</Label>
                  <Select value={form.property_id || ""} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                    <SelectTrigger data-testid="new-ticket-property"><SelectValue placeholder="Pick a property" /></SelectTrigger>
                    <SelectContent>
                      {properties.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="new-ticket-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-ticket-description" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["low", "medium", "high", "critical"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Zone</Label>
                    <Select value={form.zone_id || "_none"} onValueChange={(v) => setForm({ ...form, zone_id: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="new-ticket-zone"><SelectValue placeholder="Pick a zone" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Not applicable —</SelectItem>
                        {zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Room No.</Label>
                  <Select value={form.room_no || "_none"} onValueChange={(v) => setForm({ ...form, room_no: v === "_none" ? "" : v })}>
                    <SelectTrigger data-testid="new-ticket-room"><SelectValue placeholder="Pick a room" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Not applicable —</SelectItem>
                      {ROOM_NUMBERS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign to</Label>
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <button type="button" onClick={() => setAssignMode("individual")} className={`px-2 py-1 rounded border ${assignMode === "individual" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`} data-testid="assign-mode-individual">Individual</button>
                    <button type="button" onClick={() => setAssignMode("department")} className={`px-2 py-1 rounded border ${assignMode === "department" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200"}`} data-testid="assign-mode-department">Department</button>
                  </div>
                  {assignMode === "individual" ? (
                    <Select value={form.assigned_to || "_none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="new-ticket-assignee"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Leave unassigned —</SelectItem>
                        {assignableStaff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={form.assigned_to_department || ""} onValueChange={(v) => setForm({ ...form, assigned_to_department: v })}>
                      <SelectTrigger data-testid="new-ticket-department"><SelectValue placeholder="Pick a department" /></SelectTrigger>
                      <SelectContent>
                        {departments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-[10px] text-zinc-500 mt-1">{assignMode === "department" ? "Every member of this department will see the ticket and the first one to accept it owns it." : ""}</p>
                </div>
                <div>
                  <Label>Photos (optional)</Label>
                  <MultiPhotoUploader
                    value={form.photo_paths}
                    onChange={(paths) => setForm({ ...form, photo_paths: paths })}
                  />
                </div>
              </div>
              <DialogFooter><Button onClick={submit} className="bg-zinc-950" data-testid="save-ticket">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="ticket-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-[200px]" data-testid="ticket-filter-department"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {ticketDepartments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((t) => {
          const zone = zones.find((z) => z.id === t.zone_id);
          const assignee = staff.find((s) => s.id === t.assigned_to);
          const beforePhotos = t.photo_paths || [];
          const afterPhotos = (t.resolution_photo_paths && t.resolution_photo_paths.length)
            ? t.resolution_photo_paths
            : (t.resolution_photo ? [t.resolution_photo] : []);
          const isAssignee = t.assigned_to === user?.id;
          const canResolveThis = isManager || isAssignee;
          const canSetEta = (isAssignee || isManager) && t.assigned_to && t.status !== "resolved" && t.status !== "closed";
          return (
            <div
              key={t.id}
              className={`bg-white rounded-md p-4 ${statusBarClass(t.status)} ${t.is_overdue ? "border-2 border-red-500 ring-1 ring-red-200" : "border border-zinc-200"}`}
              data-testid={`ticket-${t.id}`}
              data-overdue={t.is_overdue ? "true" : "false"}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="label-overline">{zone?.name || "—"}</span>
                    {t.room_no && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest bg-zinc-100 text-zinc-700 rounded px-1.5 py-0.5">
                        Room {t.room_no}
                      </span>
                    )}
                    <PriorityBadge priority={t.priority} />
                    {t.assigned_to_department && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5" data-testid={`ticket-dept-${t.id}`}>
                        {t.assigned_to_department}
                      </span>
                    )}
                  </div>
                  <h4 className="font-display text-lg font-bold tracking-tight mt-1">{t.category}</h4>
                  <p className="text-sm text-zinc-600 mt-1">{t.description}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={t.status} />
                  {t.is_overdue && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white rounded px-1.5 py-0.5" data-testid={`overdue-badge-${t.id}`}>
                      <AlertTriangle className="w-2.5 h-2.5" /> Overdue
                    </span>
                  )}
                </div>
              </div>

              {/* Expected closing time */}
              {t.expected_closing_at && (
                <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] rounded px-2 py-1 ${t.is_overdue ? "bg-red-50 text-red-700" : "bg-zinc-50 text-zinc-600"}`} data-testid={`ticket-eta-${t.id}`}>
                  <CalendarClock className="w-3 h-3" />
                  Expected close: <span className="font-semibold">{fmtDateTime(t.expected_closing_at)}</span>
                </div>
              )}

              {/* Before / After collated */}
              {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <span className="label-overline">Before</span>
                    {beforePhotos.length === 0 ? (
                      <div className="mt-1 h-20 rounded border border-dashed border-zinc-200 flex items-center justify-center text-[10px] text-zinc-400">No photos</div>
                    ) : (
                      <div className="mt-1 flex gap-1 overflow-x-auto" data-testid={`before-photos-${t.id}`}>
                        {beforePhotos.map((p, i) => (
                          <img key={`b-${i}`} src={`${API}/files?path=${encodeURIComponent(p)}`} alt={`before ${i + 1}`} className="h-20 w-20 object-cover rounded border border-zinc-200 shrink-0" />
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="label-overline">After</span>
                    {afterPhotos.length === 0 ? (
                      <div className="mt-1 h-20 rounded border border-dashed border-zinc-200 flex items-center justify-center text-[10px] text-zinc-400">Pending</div>
                    ) : (
                      <div className="mt-1 flex gap-1 overflow-x-auto" data-testid={`after-photos-${t.id}`}>
                        {afterPhotos.map((p, i) => (
                          <img key={`a-${i}`} src={`${API}/files?path=${encodeURIComponent(p)}`} alt={`after ${i + 1}`} className="h-20 w-20 object-cover rounded border border-emerald-200 shrink-0" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {t.resolution_notes && <div className="mt-2 text-xs bg-emerald-50 p-2 rounded text-emerald-800">{t.resolution_notes}</div>}
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(t.created_at).toLocaleDateString()}</span>
                <span>{assignee?.name || "Unassigned"}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {isManager && (
                  <Select value={t.assigned_to || ""} onValueChange={(v) => assign(t.id, v)}>
                    <SelectTrigger className="h-8 text-xs flex-1" data-testid={`assign-ticket-${t.id}`}><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent>
                      {assignableStaff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                )}
                {!t.assigned_to && t.status !== "resolved" && t.status !== "closed" && (
                  <Button size="sm" onClick={() => acceptTicket(t.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`accept-${t.id}`}>
                    Accept ticket
                  </Button>
                )}
                {canSetEta && (
                  <Button size="sm" variant="outline" onClick={() => openEta(t)} data-testid={`set-eta-${t.id}`}>
                    <CalendarClock className="w-3.5 h-3.5 mr-1" /> {t.expected_closing_at ? "Edit close time" : "Set close time"}
                  </Button>
                )}
                {canResolveThis && t.status !== "resolved" && t.status !== "closed" && t.status !== "pending_acceptance" && (
                  <>
                    {t.status === "open" || t.status === "assigned" ? (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(t.id, "in_progress")} data-testid={`progress-${t.id}`}>Start</Button>
                    ) : null}
                    <Button size="sm" onClick={() => { setActive(t); setResOpen(true); setResolution({ resolution_notes: "", resolution_photo_paths: [] }); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`resolve-${t.id}`}>Resolve</Button>
                  </>
                )}
                {t.status === "resolved" && isManager && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(t.id, "closed")} data-testid={`close-${t.id}`}>Close</Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(t.id, "reopened")} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reopen-${t.id}`}>Reopen</Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolve Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Resolution notes</Label>
              <Textarea value={resolution.resolution_notes} onChange={(e) => setResolution({ ...resolution, resolution_notes: e.target.value })} data-testid="resolution-notes" />
            </div>
            <div>
              <Label>Proof photos (after)</Label>
              <p className="text-[11px] text-zinc-500 mb-1">Upload one or more photos showing the completed work.</p>
              <MultiPhotoUploader
                value={resolution.resolution_photo_paths}
                onChange={(paths) => setResolution({ ...resolution, resolution_photo_paths: paths })}
              />
            </div>
          </div>
          <DialogFooter><Button onClick={resolve} className="bg-emerald-600" data-testid="confirm-resolve">Mark Resolved</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!etaFor} onOpenChange={(o) => !o && setEtaFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Expected closing time</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">When do you expect to close this ticket? If it isn't resolved by then, it will be flagged overdue.</p>
            <div>
              <Label>Date &amp; time</Label>
              <Input type="datetime-local" value={etaValue} onChange={(e) => setEtaValue(e.target.value)} data-testid="eta-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEtaFor(null)}>Cancel</Button>
            <Button onClick={saveEta} className="bg-zinc-950 hover:bg-zinc-800" data-testid="eta-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MultiPhotoUploader({ value = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef();

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const ok = [];
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok.push(data.path);
        } catch (_e) {
          toast.error(`Upload failed for ${file.name || "photo"}`);
        }
      }
      if (ok.length) {
        onChange?.([...value, ...ok]);
        toast.success(`${ok.length} photo${ok.length === 1 ? "" : "s"} uploaded`);
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange?.(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid="ticket-multi-photo">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => upload(e.target.files)}
        data-testid="ticket-multi-photo-input"
      />
      <div className="flex flex-wrap gap-2">
        {value.map((p, i) => (
          <div key={`${p}-${i}`} className="relative" data-testid={`ticket-photo-preview-${i}`}>
            <img
              src={`${API}/files?path=${encodeURIComponent(p)}`}
              alt={`upload ${i + 1}`}
              className="w-24 h-24 object-cover rounded-md border border-zinc-200"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute -top-2 -right-2 bg-zinc-900 text-white rounded-full p-1"
              data-testid={`ticket-photo-remove-${i}`}
              aria-label="Remove photo"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-24 h-24 border-2 border-dashed border-zinc-300 rounded-md flex flex-col items-center justify-center text-zinc-500 hover:border-zinc-950 hover:text-zinc-950 transition-colors disabled:opacity-50"
          data-testid="ticket-multi-photo-add"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          <span className="text-[10px] mt-1 uppercase tracking-widest">{value.length === 0 ? "Add Photo" : "Add more"}</span>
        </button>
      </div>
      {value.length > 0 && (
        <p className="text-[11px] text-zinc-500">{value.length} photo{value.length === 1 ? "" : "s"} attached</p>
      )}
    </div>
  );
}
